"use client";
import { useErrorHandler } from "@/components/hooks/use-error-handler";
import { useZendeskSsoRedirect } from "@/components/hooks/use-zendesk-sso-redirect";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { DISABLE_BUTTON_DURATION_MS } from "@/constants";
import {
  type LoginError,
  type LoginFormData,
} from "@/server/actions/members/login/login";
import { pagesPath } from "@/utils/pages-path";
import { zodResolver } from "@hookform/resolvers/zod";
import ExternalLinkIcon from "@public/icons/external-link-icon.svg";
import EyeIcon from "@public/icons/eye-icon.svg";
import EyeOffIcon from "@public/icons/eye-off-icon.svg";
import TextLogo from "@public/text-logo.svg";
import Link from "next/link";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { MdCancel } from "react-icons/md";
import { z } from "zod";
import { useLogin } from "./hooks/use-login";

const loginFailureMessage = (error: LoginError): string => {
  switch (error) {
    case "invalidLoginCredentials":
    case "validationError":
      return "ログインに失敗しました。入力内容に誤りがないかご確認ください。";
    case "invalidUserKind":
      return "ログインに失敗しました。下部の「事業所の方はこちらからログイン」を選択してからもう一度お試し下さい。";
    case "userIsDeleted":
      return "このアカウントは削除されています。";
    case "alreadyLoggedIn":
      return "ログイン中のため、処理を中断しました。";
    default:
      return "ログインに失敗しました。時間をおいてもう一度お試しいただくか、サポートにお問い合わせください。";
  }
};

type LoginPageProps = {
  redirect?: string;
};

export const LoginPage = ({ redirect }: LoginPageProps) => {
  const handleError = useErrorHandler();
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const [passwordMaskEnabled, setPasswordMaskEnabled] = useState<boolean>(true);
  const { isZendeskSso, returnTo } = useZendeskSsoRedirect(redirect);

  const formSchema = z.object({
    email: z.string().min(1, {
      message: "メールアドレスが入力されていません。",
    }),
    password: z.string().min(1, {
      message: "パスワードが入力されていません。",
    }),
  });
  const form = useForm<LoginFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const login = useLogin();

  const onSubmit = form.handleSubmit(async (data) => {
    setSubmitting(true);
    const error = await login(data);
    if (error) {
      setTimeout(() => setSubmitting(false), DISABLE_BUTTON_DURATION_MS); // 連打対策
      toast({
        title: loginFailureMessage(error),
        variant: "error",
      });
      if (error === "alreadyLoggedIn") {
        handleError("ALREADY_LOGGED_IN");
      }
    }
  });

  return (
    <div className="m-6 flex h-screen flex-col items-center md:mt-24">
      <Form {...form}>
        <form onSubmit={onSubmit} className="w-full max-w-lg">
          <h1>
            <TextLogo aria-label="VCスタートアップ健康保険組合" />
          </h1>
          <Separator className="my-6 bg-monotone-2" />
          <div className="flex flex-col gap-6">
            <div className="flex flex-row justify-between">
              <h2 className="text-lg font-bold">加入者ログイン</h2>
              <Button asChild variant="primary" className="rounded-full">
                <Link href={pagesPath.members.signup.$url().path}>
                  未登録の方はこちら
                </Link>
              </Button>
            </div>
            <div className="flex flex-col gap-6">
              <FormField
                name="email"
                control={form.control}
                render={({ field, fieldState }) => (
                  <FormItem className="relative">
                    <FormLabel className="font-bold">
                      メールアドレス：
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="メールアドレス"
                        autoComplete="email"
                        type="email"
                        isInvalid={fieldState.invalid}
                        {...field}
                      />
                    </FormControl>
                    <div
                      className="absolute top-6 right-1 p-3"
                      onClick={() => {
                        form.setValue("email", "");
                      }}
                      role="button"
                      aria-label="入力したメールアドレスをクリア"
                    >
                      <MdCancel className="fill-monotone-6 stroke-white" />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="password"
                control={form.control}
                render={({ field, fieldState }) => (
                  <FormItem className="relative">
                    <FormLabel className="font-bold">パスワード：</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="パスワード"
                        autoComplete="current-password"
                        type={passwordMaskEnabled ? "password" : "text"}
                        isInvalid={fieldState.invalid}
                        {...field}
                      />
                    </FormControl>
                    <div
                      className="absolute top-6 right-0 inline-flex h-10 w-12 items-center justify-center p-2"
                      role="button"
                      onClick={() => {
                        setPasswordMaskEnabled((p) => !p);
                      }}
                      aria-label={
                        passwordMaskEnabled
                          ? "パスワードを隠す"
                          : "パスワードを表示する"
                      }
                    >
                      {passwordMaskEnabled ? (
                        <EyeIcon style={{ color: "hsl(var(--primary-500))" }} />
                      ) : (
                        <EyeOffIcon
                          style={{ color: "hsl(var(--primary-500))" }}
                        />
                      )}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="w-full text-right">
                <Link
                  href={pagesPath.members.forgot_password.$url().path}
                  className="underline"
                >
                  パスワード再発行
                </Link>
              </div>
              <div className="w-full text-right">
                <Link
                  href={
                    "https://help-top.mykenpo.com/hc/ja/articles/31994766186009-mykenpo%E3%81%B8%E3%81%AE%E7%99%BB%E9%8C%B2-%E3%83%AD%E3%82%B0%E3%82%A4%E3%83%B3%E6%96%B9%E6%B3%95#:~:text=%E3%83%AD%E3%82%B0%E3%82%A4%E3%83%B3%E3%81%AB%E5%BF%85%E8%A6%81%E3%81%AA%E3%83%A1%E3%83%BC%E3%83%AB%E3%82%A2%E3%83%89%E3%83%AC%E3%82%B9%E3%81%8C%E3%82%8F%E3%81%8B%E3%82%89%E3%81%AA%E3%81%84%E5%A0%B4%E5%90%88%EF%BC%9A%E3%81%93%E3%81%A1%E3%82%89%E3%81%AE%E3%83%95%E3%82%A9%E3%83%BC%E3%83%A0%E3%82%88%E3%82%8A%E5%81%A5%E4%BF%9D%E3%81%AB%E3%81%8A%E5%95%8F%E3%81%84%E5%90%88%E3%82%8F%E3%81%9B%E3%81%8F%E3%81%A0%E3%81%95%E3%81%84%E3%80%82"
                  }
                  target="_blank"
                  className="underline"
                >
                  登録メールアドレスがわからない場合
                  <ExternalLinkIcon className="ml-1 inline fill-monotone-9" />
                </Link>
              </div>
            </div>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="mt-4 w-full"
              disabled={form.formState.isSubmitting || submitting}
            >
              ログイン
            </Button>

            <div className="w-full text-center">
              <Link
                href={
                  isZendeskSso && returnTo
                    ? pagesPath.employers.zendesk.sso.$url({
                        query: { return_to: returnTo },
                      }).path
                    : pagesPath.employers.login.$url().path
                }
                className="underline"
              >
                事業所の方はこちらからログイン
              </Link>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};
